import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { auth } from './firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { AppDataProvider } from './components/contexts/AppDataContext';
import { SubscriptionProvider } from './components/contexts/SubscriptionContext';
import { ThemeProvider } from './components/contexts/ThemeContext';
import { ModalProvider } from './components/contexts/ModalContext';
import { KeyManagementProvider } from './components/contexts/KeyManagementContext';
import { RosterCacheProvider } from './components/contexts/RosterCacheContext';
// Force rebuild
import ErrorBoundary from './components/Shared/ErrorBoundary';
import PageTitle from './components/Shared/PageTitle';

// Lazy Load Heavy Components
const ProtectedDashboard = lazy(() => import('./components/ProtectedDashboard'));
const PublicToolsPage = lazy(() => import('./components/Public/PublicToolsPage'));
const WordSearchGenerator = lazy(() => import('./components/Games/WordSearchGenerator'));
const CrosswordGenerator = lazy(() => import('./components/Games/CrosswordGenerator'));
const BingoGenerator = lazy(() => import('./components/Games/BingoGenerator'));
const Clock = lazy(() => import('./components/ClassroomTools/Clock'));
const PublicJoinPage = lazy(() => import('./components/Public/PublicJoinPage'));
// PublicWordWiggle removed
const NoiseMeter = lazy(() => import('./components/ClassroomTools/NoiseMeter'));
const Timer = lazy(() => import('./components/ClassroomTools/Timer'));
const HomePage = lazy(() => import('./components/Public/HomePage'));
const LoginPage = lazy(() => import('./components/Public/LoginPage'));
const SignUpPage = lazy(() => import('./components/Public/SignUpPage'));
const NotFound = lazy(() => import('./components/Public/NotFound'));
const FeaturesPage = lazy(() => import('./components/Public/FeaturesPage'));
const PricingPage = lazy(() => import('./components/Public/PricingPage'));
const ContactPage = lazy(() => import('./components/Public/ContactPage'));
const TermsOfServicePage = lazy(() => import('./components/Public/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('./components/Public/PrivacyPolicyPage'));
const SplashScreen = lazy(() => import('./components/Public/SplashScreen'));
const TutorialHub = lazy(() => import('./components/Public/Tutorials/TutorialHub'));

import PublicLayout from './components/Public/PublicLayout';
import LoadingSpinner from './components/Shared/LoadingSpinner';
import { GoogleOAuthCallback } from './components/Management/GoogleOAuthCallback';

import { AuthProvider, useAuth } from '@/components/contexts/AuthContext';
import { SessionProvider, useSession } from './components/contexts/SessionContext';
import SessionWarningModal from './components/Shared/SessionWarningModal';
import ReAuthenticationModal from './components/Shared/ReAuthenticationModal';
const AppContent: React.FC = () => {
    const { user, loading } = useAuth();

    const SessionGuard = ({ children }: { children: React.ReactNode }) => {
        const { isLocked } = useSession();
        if (isLocked) {
            return <ReAuthenticationModal />;
        }
        return (
            <>
                {children}
                <SessionWarningModal />
                <ReAuthenticationModal />
            </>
        );
    };

    // Protected Route Wrapper
    const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
        if (loading) {
            return <LoadingSpinner />;
        }
        if (!user) {
            return <Navigate to="/login" replace />;
        }
        return (
            <SessionProvider>
                <SessionGuard>
                    {children}
                </SessionGuard>
            </SessionProvider>
        );
    };


    return (
        <ErrorBoundary>
            <ThemeProvider>
                <AppDataProvider>
                    <KeyManagementProvider>
                        <RosterCacheProvider>
                            <SubscriptionProvider>
                                <ModalProvider>
                            <Router>
                                <Routes>
                        {/* Standalone Route (No Navbar) - QUIZ DISABLED 
                        <Route path="/join" element={
                            <PageTitle title="Join Game">
                                <PublicJoinPage />
                            </PageTitle>
                        } />
                        */}

                        <Route path="/oauth-callback" element={
                            <PageTitle title="Linking Google Classroom">
                                <GoogleOAuthCallback />
                            </PageTitle>
                        } />

                        <Route path="/splash" element={
                            <PageTitle title="Splash Screen">
                                <Suspense fallback={<LoadingSpinner />}>
                                    <SplashScreen />
                                </Suspense>
                            </PageTitle>
                        } />

                        {/* Public Routes Wrapped in Layout */}
                        <Route element={<PublicLayout />}>
                            <Route path="/" element={
                                <PageTitle 
                                    title="Home" 
                                    description="Stop drowning in teacher paperwork. PocketProf is an elegant assessment and classroom management toolkit designed by teachers for teachers. Log observations, conversations, and triangulate data instantly."
                                >
                                    <HomePage />
                                </PageTitle>
                            } />
                            <Route path="/features" element={
                                <PageTitle 
                                    title="Features"
                                    description="Explore our suite of teacher tools: Instant rubric sync, automated report card comment builders, ALP goal generators, and deep student performance analytics."
                                >
                                    <FeaturesPage />
                                </PageTitle>
                            } />
                            <Route path="/pricing" element={
                                <PageTitle 
                                    title="Pricing"
                                    description="Affordable assessment and reporting tools for educators. Start for free and upgrade for unlimited observations, conversations, and premium teacher tools."
                                >
                                    <PricingPage />
                                </PageTitle>
                            } />
                            <Route path="/contact" element={
                                <PageTitle 
                                    title="Contact Us"
                                    description="Have questions about PocketProf? Get in touch with our team of educators. We're here to help you streamline your assessment and reporting."
                                >
                                    <ContactPage />
                                </PageTitle>
                            } />
                            <Route path="/login" element={
                                <PageTitle title="Login">
                                    {user ? <Navigate to="/app" /> : <LoginPage onLogin={() => { }} />}
                                </PageTitle>
                            } />
                            <Route path="/signup" element={
                                <PageTitle title="Create Account">
                                    {user ? <Navigate to="/app" /> : <SignUpPage onLogin={() => { }} />}
                                </PageTitle>
                            } />
                            <Route path="/terms" element={
                                <PageTitle title="Terms of Service & AUP">
                                    <TermsOfServicePage />
                                </PageTitle>
                            } />
                            <Route path="/privacy" element={
                                <PageTitle title="Privacy Policy">
                                    <PrivacyPolicyPage />
                                </PageTitle>
                            } />

                            {/* Public Tools */}
                            <Route path="/tools" element={
                                <PageTitle 
                                    title="Free Classroom Tools"
                                    description="Access our library of free teacher utilities: Word search makers, crossword generators, classroom noise meters, and student group makers."
                                >
                                    <PublicToolsPage />
                                </PageTitle>
                            } />
                            <Route path="/tools/word-search" element={
                                <PageTitle 
                                    title="Free Printable Word Search Generator for Teachers"
                                    description="Create custom, printable word search puzzles for your classroom in seconds. 100% ad-free and print-ready worksheet generator."
                                    schema={{
                                        "@context": "https://schema.org",
                                        "@type": "WebApplication",
                                        "name": "Free Word Search Maker for Teachers",
                                        "description": "Create professional, printable word search puzzles for your classroom in seconds. Fully customizable teacher tool for all grade levels.",
                                        "applicationCategory": "EducationalApplication",
                                        "operatingSystem": "All",
                                        "browserRequirements": "Requires HTML5 and JavaScript Support."
                                    }}
                                >
                                    <WordSearchGenerator />
                                </PageTitle>
                            } />
                            <Route path="/tools/crossword" element={
                                <PageTitle 
                                    title="Free Custom Crossword Puzzle Maker & Generator for Teachers"
                                    description="Generate custom, printable crossword puzzles for your students in seconds. 100% ad-free review worksheet creator for any grade level."
                                    schema={{
                                        "@context": "https://schema.org",
                                        "@type": "WebApplication",
                                        "name": "Free Crossword Puzzle Maker for Teachers",
                                        "description": "Generate custom, printable crossword puzzles for your students in seconds. A perfect vocabulary and review tool for any grade level.",
                                        "applicationCategory": "EducationalApplication",
                                        "operatingSystem": "All",
                                        "browserRequirements": "Requires HTML5 and JavaScript Support."
                                    }}
                                >
                                    <CrosswordGenerator />
                                </PageTitle>
                            } />
                            <Route path="/tools/bingo" element={
                                <PageTitle 
                                    title="Free Classroom Bingo Card Generator (Printable PDF Maker)"
                                    description="Create custom, printable bingo cards for your classroom instantly. A fun, 100% ad-free way to review vocabulary, terms, or numbers."
                                    schema={{
                                        "@context": "https://schema.org",
                                        "@type": "WebApplication",
                                        "name": "Free Classroom Bingo Maker",
                                        "description": "Create custom bingo cards for your classroom instantly. A fun, interactive way to review terms, numbers, or concepts with your students.",
                                        "applicationCategory": "EducationalApplication",
                                        "operatingSystem": "All",
                                        "browserRequirements": "Requires HTML5 and JavaScript Support."
                                    }}
                                >
                                    <BingoGenerator />
                                </PageTitle>
                            } />
                            <Route path="/tools/clock" element={
                                <PageTitle 
                                    title="Free Classroom Clock & Online Activity Timer"
                                    description="A clean, visual classroom clock and timer tool for teachers. 100% ad-free and display-ready."
                                    schema={{
                                        "@context": "https://schema.org",
                                        "@type": "WebApplication",
                                        "name": "Classroom Clock",
                                        "description": "A clean, visual classroom clock tool for teachers.",
                                        "applicationCategory": "EducationalApplication",
                                        "operatingSystem": "All",
                                        "browserRequirements": "Requires HTML5 and JavaScript Support."
                                    }}
                                >
                                    <Clock />
                                </PageTitle>
                            } />

                            <Route path="/tools/noise-meter" element={
                                <PageTitle 
                                    title="Free Classroom Noise Level Meter & Visual Decibel Tracker"
                                    description="Use our visual decibel noise meter to help students self-regulate classroom volume. An online, ad-free sound monitor for a calm learning environment."
                                    schema={{
                                        "@context": "https://schema.org",
                                        "@type": "WebApplication",
                                        "name": "Free Classroom Noise Meter",
                                        "description": "Use our visual noise meter to help students self-regulate classroom volume. A simple, effective management tool for a calm learning environment.",
                                        "applicationCategory": "EducationalApplication",
                                        "operatingSystem": "All",
                                        "browserRequirements": "Requires HTML5 and JavaScript Support."
                                    }}
                                >
                                    <div className="max-w-7xl mx-auto px-4 py-8">
                                        <NoiseMeter />
                                    </div>
                                </PageTitle>
                            } />
                            <Route path="/tools/timer" element={
                                <PageTitle 
                                    title="Free Classroom Countdown Timer & Visual Activity Clock"
                                    description="Keep activities on track with our clean, visual classroom countdown timer. 100% ad-free and perfect for transitions, centers, and timed tests."
                                    schema={{
                                        "@context": "https://schema.org",
                                        "@type": "WebApplication",
                                        "name": "Free Classroom Timer",
                                        "description": "Keep your activities on track with our clean, visual classroom timer. Perfect for transitions, centers, and timed assessments.",
                                        "applicationCategory": "EducationalApplication",
                                        "operatingSystem": "All",
                                        "browserRequirements": "Requires HTML5 and JavaScript Support."
                                    }}
                                >
                                    <div className="max-w-7xl mx-auto px-4 py-8">
                                        <Timer />
                                    </div>
                                </PageTitle>
                            } />

                            <Route path="/tutorials" element={
                                <PageTitle 
                                    title="Tutorial Hub"
                                    description="Learn how to get the most out of PocketProf with our step-by-step guides and video tutorials."
                                >
                                    <TutorialHub />
                                </PageTitle>
                            } />

                            {/* 404 Catch-All (inside layout so it gets navbar) */}
                            <Route path="*" element={
                                <PageTitle title="Page Not Found">
                                    <NotFound />
                                </PageTitle>
                            } />
                        </Route>

                        {/* Protected Dashboard Routes (No Public Layout) */}
                        <Route
                            path="/app/*"
                            element={
                                <PageTitle title="Dashboard">
                                    <ProtectedRoute>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <ProtectedDashboard />
                                        </Suspense>
                                    </ProtectedRoute>
                                </PageTitle>
                            }
                        />
                            </Routes>
                        </Router>
                    </ModalProvider>
                </SubscriptionProvider>
            </RosterCacheProvider>
        </KeyManagementProvider>
    </AppDataProvider>
    </ThemeProvider>
        </ErrorBoundary>
    );
};

const MainApp: React.FC = () => {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
};

export default MainApp;
